import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  ArrowUpRight,
  Check,
  CheckCircle2,
  DoorOpen,
  Heart,
  Home,
  Lock,
  MessagesSquare,
  Music,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  ButtonLink,
  CardGrid,
  CheckList,
  Container,
  FeatureCard,
  FeatureRow,
  IconChip,
  Lead,
  Reveal,
  Section,
  SectionHeader,
  Steps,
  TextLink,
  Card,
  PageHero,
} from '../components/page';
import { Avatar, Glow } from '../components/ui';
import SmartLink from '../components/SmartLink';

/* ------------------------------------------------------------------ */
/* Mock product visuals                                                */
/* ------------------------------------------------------------------ */

const MockFrame = ({ children, glow = 'bg-brand-500/15' }) => (
  <div className="relative">
    <Glow className={clsx('-inset-8', glow)} />
    <div className="surface relative !bg-ink-900/90 p-6 sm:p-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
      {children}
    </div>
  </div>
);

const MockHeader = ({ icon: Icon, title, subtitle, badge, badgeClass }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex items-center gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.06] text-white">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </div>
    {badge && (
      <span className={clsx('rounded-full border px-3 py-1 text-xs font-medium', badgeClass)}>{badge}</span>
    )}
  </div>
);

const APPROVERS = [
  { initials: 'JD', color: 'blue' },
  { initials: 'KS', color: 'green' },
  { initials: 'MR', color: 'amber' },
];

const ApprovalVisual = () => (
  <MockFrame glow="bg-emerald-500/15">
    <MockHeader
      icon={ArrowUpRight}
      title="Withdrawal Request"
      subtitle="Shared Rent · Apartment 4B"
      badge="Everyone Agrees"
      badgeClass="border-white/10 bg-white/[0.04] text-slate-300"
    />
    <p className="mt-8 text-5xl font-extrabold tabular-nums tracking-tight text-white">$1,800.00</p>
    <p className="mt-2 text-sm text-slate-400">March rent · requested by Mike R.</p>
    <div className="surface-inset mt-8 p-5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-white">Approvals</span>
        <span className="tabular-nums text-emerald-300">3 of 3</span>
      </div>
      <div className="mt-5 flex items-center gap-4">
        {APPROVERS.map((m, i) => (
          <motion.div
            key={m.initials}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.25, type: 'spring', stiffness: 400, damping: 20 }}
            className="relative rounded-full bg-emerald-400 p-[3px] shadow-[0_0_24px_-2px_rgba(52,211,153,0.6)]"
          >
            <Avatar initials={m.initials} color={m.color} />
            <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-400 text-ink-950 ring-2 ring-ink-900">
              <Check className="h-3 w-3" strokeWidth={4} />
            </span>
          </motion.div>
        ))}
      </div>
    </div>
    <div className="mt-5 flex items-center gap-2 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-3.5 text-sm font-semibold text-emerald-300">
      <CheckCircle2 className="h-5 w-5" />
      Approved · funds released
    </div>
  </MockFrame>
);

const MediationVisual = () => (
  <MockFrame glow="bg-violet-500/15">
    <MockHeader
      icon={MessagesSquare}
      title="Resolution Center"
      subtitle="Festival Fund · Private case"
      badge="Confidential"
      badgeClass="border-violet-400/20 bg-violet-400/10 text-violet-300"
    />
    <div className="mt-8 space-y-3">
      <div className="flex gap-3">
        <Avatar initials="KS" color="green" />
        <div className="flex-1 rounded-2xl rounded-tl-sm bg-white/[0.04] p-3.5 text-sm text-slate-300">
          I missed my contribution this month—can we sort it out?
        </div>
      </div>
      <div className="flex gap-3">
        <Avatar initials="AL" color="pink" />
        <div className="flex-1 rounded-2xl rounded-tl-sm bg-white/[0.04] p-3.5 text-sm text-slate-300">
          Happy to talk it through. I just want it to be fair for everyone.
        </div>
      </div>
      <div className="rounded-2xl border border-brand-400/25 bg-brand-400/[0.07] p-3.5">
        <p className="flex items-center gap-2 text-sm font-medium text-white">
          <Sparkles className="h-4 w-4 text-brand-300" />
          Groupmoney Mediator
        </p>
        <p className="mt-1.5 text-sm text-slate-300">
          Thanks, both. Let's agree on a catch-up plan that works for the whole group.
        </p>
      </div>
    </div>
  </MockFrame>
);

const ContinuityVisual = () => (
  <MockFrame glow="bg-amber-500/10">
    <MockHeader
      icon={ScrollText}
      title="Continuity Directive"
      subtitle="Family Fund"
      badge="Active"
      badgeClass="border-emerald-400/20 bg-emerald-400/10 text-emerald-300"
    />
    <div className="mt-8 space-y-3">
      <div className="surface-inset flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <Avatar initials="SM" color="purple" />
          <div>
            <p className="text-xs text-slate-500">Designated successor</p>
            <p className="font-semibold text-white">Sarah Miller</p>
          </div>
        </div>
        <UserCheck className="h-5 w-5 text-emerald-300" />
      </div>
      <div className="surface-inset p-4">
        <p className="text-xs text-slate-500">Distribution rule</p>
        <p className="mt-1 font-semibold text-white">Split equally among remaining members</p>
        <div className="mt-4 flex h-2 gap-1 overflow-hidden rounded-full">
          <span className="flex-1 bg-brand-400" />
          <span className="flex-1 bg-brand-400/70" />
          <span className="flex-1 bg-brand-400/40" />
        </div>
      </div>
    </div>
    <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-500">
      <Lock className="h-3.5 w-3.5" />
      Stored securely with your group record
    </p>
  </MockFrame>
);

const GROUP_RULES = [
  { icon: UserPlus, name: 'Invite Power', value: 'Most Agree' },
  { icon: SlidersHorizontal, name: 'Rule Shift', value: 'Everyone Agrees' },
  { icon: DoorOpen, name: 'Exit Gate', value: 'Key Voters' },
];

const RulesVisual = () => (
  <MockFrame>
    <MockHeader icon={Users} title="Group Rules" subtitle="Group Trip Fund · Hawaii 2025" />
    <div className="mt-8 space-y-3">
      {GROUP_RULES.map(({ icon: Icon, name, value }, i) => (
        <motion.div
          key={name}
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 + i * 0.12, duration: 0.5 }}
          className="surface-inset flex items-center justify-between gap-3 p-4"
        >
          <span className="flex items-center gap-3 text-sm font-medium text-white">
            <IconChip icon={Icon} size="sm" />
            {name}
          </span>
          <span className="rounded-full border border-brand-400/20 bg-brand-400/10 px-3 py-1 text-xs font-semibold text-brand-300">
            {value}
          </span>
        </motion.div>
      ))}
    </div>
  </MockFrame>
);

/* ------------------------------------------------------------------ */
/* Copy                                                                */
/* ------------------------------------------------------------------ */

const MATTERS = [
  { icon: ShieldCheck, bold: 'Knowing your funds are safe', rest: ' and spent only with approval.' },
  { icon: MessagesSquare, bold: 'Having a trusted mediator', rest: ' for any disagreements.' },
  { icon: ScrollText, bold: "Being prepared for life's uncertainties", rest: ', like a member passing away.' },
  { icon: SlidersHorizontal, bold: 'Staying in control', rest: " with rules that fit your group's dynamic." },
];

const EXAMPLES = [
  {
    icon: Home,
    accent: 'emerald',
    title: 'Roommates Splitting Rent',
    text: 'Use Group Controls to ensure all roommates approve rent payments, and rely on the Resolution Center if someone misses a contribution.',
  },
  {
    icon: Music,
    accent: 'amber',
    title: 'Friends Saving for a Festival',
    text: 'Set "Key Voters" for spending, and use the Continuity Directive to plan for unexpected member changes.',
  },
  {
    icon: Heart,
    accent: 'pink',
    title: 'Family Emergency Fund',
    text: 'Choose "Everyone Agrees" for withdrawals, and feel secure knowing the Continuity Directive protects your savings.',
  },
];

const REASONS = [
  { icon: Lock, title: 'Total Security', text: 'From Group Controls to encryption, your funds and data are safe.' },
  { icon: MessagesSquare, title: 'Conflict-Free Collaboration', text: 'The Resolution Center resolves issues while preserving relationships.' },
  { icon: ScrollText, title: 'Prepared for Anything', text: 'The Continuity Directive ensures clarity in tough times.' },
  { icon: SlidersHorizontal, title: "You're in Charge", text: 'Customizable rules let you set the tone for your group.' },
];

const GET_STARTED = [
  { title: 'Sign Up for Free', text: 'Create your account in minutes.' },
  { title: 'Set Your Rules', text: 'Customize Group Controls and other Group Rules.' },
  { title: 'Enjoy the Journey', text: 'Let us handle the details while you focus on your goal.' },
];

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const PeaceOfMindPage = () => (
  <>
    <PageHero
      highlight="Peace of Mind"
      title="Your Group, Your Rules, Your Security"
      description="Focus on your shared goals—we've got your back."
    />

    <Section className="!pt-8 sm:!pt-12">
      <Container>
        <SectionHeader title="Trust Groupmoney to Keep Your Group Thriving" />
        <Lead className="mt-8">
          At Groupmoney, we know that pooling money with others can come with worries—Will everyone agree on spending?
          What if there's a conflict? What happens if something unexpected occurs? That's why we've built a platform
          centered on Peace of Mind. With features like Group Controls, our Resolution Center, Continuity Directive, and
          customizable group rules, we ensure your group's finances are secure, transparent, and conflict-free. Focus on
          your shared goals—we've got your back.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Why It Matters"
          title="Why Peace of Mind Matters"
          description="When you're managing money as a group—whether it's for a trip, shared bills, or a family fund—you need to feel confident that everything will go smoothly. Peace of Mind means:"
        />
        <CardGrid cols={4} className="mt-16 sm:mt-20">
          {MATTERS.map(({ icon, bold, rest }) => (
            <Card key={bold}>
              <IconChip icon={icon} />
              <p className="mt-6 leading-relaxed text-slate-400">
                <span className="font-semibold text-white">{bold}</span>
                {rest}
              </p>
            </Card>
          ))}
        </CardGrid>
        <Lead className="mt-16">
          With Groupmoney, you can relax and enjoy the journey, knowing we've designed every feature to keep your group
          secure and united.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Core Features"
          title="How We Deliver Peace of Mind"
          description="Here's how our core features work together to give you confidence at every step:"
        />
        <div className="mt-20 space-y-24 sm:mt-24 sm:space-y-32">
          <FeatureRow
            icon={ShieldCheck}
            eyebrow="Group Controls"
            title="Group Controls: You Decide Who Spends"
            visual={<ApprovalVisual />}
          >
            <p>
              Worried about unauthorized withdrawals? Group Controls lets you set the rules for how money is spent.
              Choose who approves withdrawals—whether it's everyone, a majority, a pair, or specific "Key Voters." This
              ensures funds are only used when your group agrees, giving you:
            </p>
            <CheckList
              className="text-base"
              items={[
                { label: 'Security', text: 'No surprises—every withdrawal follows your rules.' },
                { label: 'Transparency', text: 'Everyone knows the approval process upfront.' },
                { label: 'Flexibility', text: "Customize settings to match your group's trust level." },
              ]}
            />
            <TextLink to="/group-controls">Learn more about Group Controls</TextLink>
          </FeatureRow>

          <FeatureRow
            reverse
            icon={MessagesSquare}
            accent="violet"
            eyebrow="Resolution Center"
            title="Resolution Center: Resolve Conflicts with Care"
            visual={<MediationVisual />}
          >
            <p>
              Disputes can strain relationships, but they don't have to. Our Resolution Center is here to mediate
              conflicts with a people-first approach. Whether it's a disagreement over contributions or spending, we:
            </p>
            <CheckList
              className="text-base"
              items={[
                { label: 'Prioritize Relationships', text: 'We focus on solutions that preserve group harmony.' },
                { label: 'Keep It Private', text: 'All discussions are confidential—we respect your trust.' },
                { label: 'Act Quickly', text: "Start the process in-app, and we'll engage immediately to find a fair resolution." },
              ]}
            />
            <p className="text-base text-slate-500">
              Available with basic access for Free users (guides, 1 chat/month) and full live support for Basic and Max
              users.
            </p>
            <TextLink to="/resolution-center">Learn more about Resolution Center</TextLink>
          </FeatureRow>

          <FeatureRow
            icon={ScrollText}
            accent="amber"
            eyebrow="Continuity Directive"
            title="Continuity Directive: Plan for the Unexpected"
            visual={<ContinuityVisual />}
          >
            <p>
              Life can be unpredictable, and we've got you covered. The Continuity Directive ensures your group's funds
              are handled responsibly if a member passes away. You can:
            </p>
            <CheckList
              className="text-base"
              items={[
                { label: 'Designate a Successor', text: 'Name someone to manage the account.' },
                { label: 'Set Clear Rules', text: 'Decide how funds are distributed in advance.' },
                { label: 'Protect Your Group', text: 'Avoid confusion during difficult times.' },
              ]}
            />
            <p className="text-base text-slate-500">
              This feature, available across all tiers, gives you confidence that your group's goals are secure, no
              matter what happens.
            </p>
          </FeatureRow>

          <FeatureRow
            reverse
            icon={SlidersHorizontal}
            eyebrow="Group Rules"
            title="Control at Every Step: Customize Your Group Rules"
            visual={<RulesVisual />}
          >
            <p>
              Beyond Group Controls, Groupmoney lets you set rules for every aspect of your group's operations. With
              Group Rules, you can:
            </p>
            <CheckList
              className="text-base"
              items={[
                'Decide who can invite members ("Invite Power").',
                'Approve changes to settings ("Rule Shift").',
                'Manage member exits ("Exit Gate").',
              ]}
            />
            <p>
              Choose from options like "Everyone Agrees," "Most Agree," or "Key Voters" to tailor control to your group's
              needs. You're always in charge, ensuring clarity and fairness for everyone.
            </p>
          </FeatureRow>
        </div>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Real Groups"
          title="Real-Life Peace of Mind"
          description="Here's how these features bring confidence to different groups:"
        />
        <CardGrid cols={3} className="mt-16 sm:mt-20">
          {EXAMPLES.map(({ icon, accent, title, text }) => (
            <FeatureCard key={title} icon={icon} accent={accent} title={title}>
              {text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Lead className="mt-16">
          No matter your group's purpose, Peace of Mind means you can focus on what matters—achieving your goal together.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader eyebrow="The Difference" title="Why Choose Groupmoney for Peace of Mind?" />
        <CardGrid cols={4} className="mt-16 sm:mt-20">
          {REASONS.map(({ icon, title, text }) => (
            <FeatureCard key={title} icon={icon} title={title}>
              {text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Lead className="mt-16">
          With Groupmoney, Peace of Mind isn't just a promise—it's built into everything we do.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Get Started"
          title="Experience Peace of Mind Today"
          description="Ready to manage your group's finances with confidence? It's easy to get started:"
        />
        <Steps steps={GET_STARTED} className="mt-16 sm:mt-20" />
        <Reveal className="mt-20 text-center">
          <p className="text-xl text-slate-300">Join thousands of groups already saving with Peace of Mind.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <ButtonLink to="/#get-started">Sign Up Now</ButtonLink>
            <ButtonLink to="/#features" variant="secondary">
              Explore Features
            </ButtonLink>
          </div>
          <p className="mt-8 text-sm text-slate-500">
            Have questions? Visit the{' '}
            <SmartLink to="/resolution-center" className="font-medium text-brand-300 hover:text-white">
              Resolution Center
            </SmartLink>{' '}
            or email us at{' '}
            <a href="mailto:support@groupmoney.app" className="font-medium text-brand-300 hover:text-white">
              support@groupmoney.app
            </a>
            .
          </p>
        </Reveal>
      </Container>
    </Section>
  </>
);

export default PeaceOfMindPage;
